import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, Collapse, Typography, useMediaQuery, useTheme } from '@mui/material';
import { RootState } from '../../app/store';
import type { Foodstuff } from '../../models/Foodstuff';
import { Gauge } from './Gauge';
import { useTranslation } from 'react-i18next';
import { emptyDailyNutrients } from '../../common/nutritionTargets';
import { uiTokens } from '../../common/uiTokens';


export function Gauges() {
    const user = useSelector((state: RootState) => state.user.user);
    const consumedMeals = useSelector((state: RootState) => state.meal.consumedMeals);
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [showDetails, setShowDetails] = useState(false);

    const targetValues = () => {
        const directDaily = user?.dailyNutrients;
        if (directDaily) {
            return {
                ENERGY: Number(directDaily.ENERGY ?? 0),
                PROTEIN: Number(directDaily.PROTEIN ?? 0),
                FAT: Number(directDaily.FAT ?? 0),
                CARBOHYDRATE: Number(directDaily.CARBOHYDRATE ?? 0),
            };
        }

        return emptyDailyNutrients();
    };

    const resolveWeight = (foodstuff: Foodstuff) => {
        const directWeight = Number(foodstuff.weight);
        if (Number.isFinite(directWeight) && directWeight > 0) {
            return directWeight;
        }

        const value = Number(foodstuff.value);
        if (!Number.isFinite(value) || value <= 0) {
            return 0;
        }

        const conversionFactor = foodstuff.measurementUnits?.find((unit) => unit.selected)?.conversionFactor;
        if (Number.isFinite(conversionFactor) && (conversionFactor as number) > 0) {
            return value * (conversionFactor as number);
        }

        return value;
    };

    const consumedValues = () => {
        const totals = { ENERGY: 0, PROTEIN: 0, FAT: 0, CARBOHYDRATE: 0 };

        consumedMeals?.forEach((meal) => {
            const mealFoodstuffs = (meal.foodstuffs && meal.foodstuffs.length > 0)
                ? meal.foodstuffs
                : meal.meal?.foodstuffs;
            mealFoodstuffs?.forEach((f) => {
                const weight = resolveWeight(f);
                totals.ENERGY += (f.energyInKcal || 0) * weight;
                totals.PROTEIN += (f.proteinMassFraction || 0) * weight;
                totals.FAT += (f.fatMassFraction || 0) * weight;
                totals.CARBOHYDRATE += (f.carbohydrateMassFraction || 0) * weight;
            });
        });

        return totals;
    };

    const targets = targetValues();
    const consumed = consumedValues();

    const nutrients = ['ENERGY', 'PROTEIN', 'FAT', 'CARBOHYDRATE'] as const;
    const primaryNutrients: readonly typeof nutrients[number][] = isMobile ? ['ENERGY'] : nutrients;
    const detailNutrients = ['PROTEIN', 'FAT', 'CARBOHYDRATE'] as const;

    const hasLoggedMeals = (consumedMeals?.length || 0) > 0;

    return (
        <Box>
            {!hasLoggedMeals && (
                <Typography variant="body2" sx={{ textAlign: 'center', color: '#9C948C', mt: 1 }}>
                    {t('client_dashboard.no_meals_logged')}
                </Typography>
            )}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: {
                        xs: 'column',
                        md: 'row'
                    },
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: { xs: 3, md: 4 },
                    mt: 2,
                    opacity: hasLoggedMeals ? 1 : 0.55,
                }}
            >
                {primaryNutrients.map((nutrient) => {
                    const targetVal = Number((targets[nutrient] ?? 0).toFixed(1));
                    const consumedVal = Number((consumed[nutrient] ?? 0).toFixed(1));

                    return (
                        <Box
                            key={nutrient}
                            sx={{
                                backgroundColor: uiTokens.colors.surfaceMuted,
                                border: `1px solid ${uiTokens.colors.border}`,
                                borderRadius: 4,
                                px: { xs: 1, md: 1.5 },
                                py: { xs: 1, md: 1.5 },
                            }}
                        >
                            <Gauge
                                nutrient={nutrient}
                                actualValue={consumedVal}
                                targetValue={targetVal}
                                isEmpty={!hasLoggedMeals}
                                size={isMobile ? 180 : 220}
                            />
                        </Box>
                    );
                })}
            </Box>
            {isMobile && (
                <>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Button
                            variant="text"
                            onClick={() => setShowDetails((prev) => !prev)}
                            sx={{ textTransform: 'none' }}
                        >
                            {showDetails ? t('client_dashboard.collapse_details') : t('client_dashboard.expand_details')}
                        </Button>
                    </Box>
                    <Collapse in={showDetails} timeout="auto" unmountOnExit>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 3,
                                mt: 2,
                            }}
                        >
                            {detailNutrients.map((nutrient) => {
                                const targetVal = Number((targets[nutrient] ?? 0).toFixed(1));
                                const consumedVal = Number((consumed[nutrient] ?? 0).toFixed(1));

                                return (
                                    <Box
                                        key={nutrient}
                                        sx={{
                                            backgroundColor: uiTokens.colors.surfaceMuted,
                                            border: `1px solid ${uiTokens.colors.border}`,
                                            borderRadius: 4,
                                            px: 1,
                                            py: 1,
                                        }}
                                    >
                                        <Gauge
                                            nutrient={nutrient}
                                            actualValue={consumedVal}
                                            targetValue={targetVal}
                                            isEmpty={!hasLoggedMeals}
                                            size={145}
                                        />
                                    </Box>
                                );
                            })}
                        </Box>
                    </Collapse>
                </>
            )}
        </Box>
    );
};
